'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import Image from 'next/image';
import cx from 'classnames';
import { toInteger } from 'lodash';
// Utils
import { Account, Feature, GalleryAsset, Listing, Maybe, PropertyType, SearchPromotionType, PriceDisplay } from '@gql';
import { useWindowSize } from '@hooks';
import { GRAY_PIXEL_DATA_URL, enumToStringPropertyTypesAbr, enumToStringUniquePropertyTypes } from '@utils/constants';
import { formatWholeDollarAmount } from '@utils/money';
import { imagekitLoader, imagekitProgressiveLoader } from '@utils/imagekit-loader';
import { concatenateAndReplace } from '@utils/test-helpers';
import { formatDateMMMD } from '@utils/date-helpers';
import { formatListingLocation } from '@utils/format-listing-location';
import { useAnalytics } from '@contexts/use-analytics';
import { AnalyticsRef } from '@components/analytics-ref';

// UI
import { FavoriteButton } from '@components/favorite-button';
import { IconNames } from '@components/icon';
import { IconTooltip } from '@components/icon-tooltip';
import { Link } from '@components/link';
import { Carousel, type CarouselRef } from '@components/carousel';
export type ListingPartial = Pick<Listing, 'bookDirectWebsiteUrl' | 'city' | 'description' | 'marketingWebsiteUrl' | 'name' | 'slug' | 'stateOrProvinceLongName' | 'guestCapacity' | 'numberOfRooms' | 'numberOfBathrooms' | 'minPrice' | 'maxPrice' | 'propertyType' | 'uniquePropertyType' | 'id' | 'isFavorited' | 'listingLevel' | 'searchableAvailability' | 'isDiamondCollection' | 'searchPromotionType' | 'reviewsAverageRating' | 'reviewsCount' | 'averageSavings'> & {
  galleryAssets: Array<Pick<GalleryAsset, 'id' | 'url' | 'description'>>;
  priceDisplay?: Pick<PriceDisplay, 'baseAmountCents' | 'checkInDate' | 'checkOutDate' | 'nights' | 'subtotalWithoutTaxesCents'>;
  features?: Maybe<Array<Pick<Feature, 'key' | 'displayName' | 'categoryName' | 'promoted'>>>;
  account: Pick<Account, 'companyName' | 'slug' | 'avatarUrl'>;
};
const rateBeforeSavings = (nightlyRate: number): number => {
  return nightlyRate * 1.15;
};
const formatSavings = (savings: number | null | undefined): string | null => {
  if (!savings || savings <= 0) return null;
  return `${Math.round(savings)}%`;
};
interface SavingsPillProps {
  savings: number | null | undefined;
  featured?: boolean;
}
const SavingsPill: React.FC<SavingsPillProps> = t0 => {
  const $ = _c(6);
  const {
    savings
  } = t0;
  if (!savings || savings <= 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx("absolute left-2 z-40 bg-sunrise text-primary-main text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1", "top-3.5");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== savings) {
    t2 = formatSavings(savings);
    $[1] = savings;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IconTooltip icon={IconNames.INFO_CIRCLE} size="xs" placement="bottom" className="text-primary-main mt-0.5">What you save on this property by booking direct through Savvy. Averages may vary.</IconTooltip>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <div className={t1}>{t2} savings{t3}</div>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export interface IListingCardProps {
  listing: ListingPartial;
  isFavorited: boolean;
  altTag: string;
  showRoute: string;
  accountShowRoute?: string;
  forceMobileView?: boolean;
  displayStay?: boolean;
  avatarUrl?: string;
  priority?: boolean;
  onFavoritedSuccess?: (isFavorited: boolean) => void;
  isHomePage?: boolean;
  isHomePageMobile?: boolean;
  /** Extra classes for the card container (e.g. a white surface on a dark band). */
  containerClassName?: string;
}
type SliderImage = {
  url: string;
};
const smallScreenSize = 640;
export const ListingCard: React.FC<IListingCardProps> = t0 => {
  const $ = _c(159);
  const {
    listing,
    altTag,
    isFavorited,
    showRoute,
    forceMobileView: t1,
    displayStay: t2,
    priority,
    onFavoritedSuccess,
    isHomePage: t3,
    isHomePageMobile: t4,
    containerClassName
  } = t0;
  const forceMobileView = t1 === undefined ? false : t1;
  const displayStay = t2 === undefined ? false : t2;
  const isHomePage = t3 === undefined ? false : t3;
  const isHomePageMobile = t4 === undefined ? false : t4;
  const featured = listing.searchPromotionType === SearchPromotionType.Featured;
  const {
    width: screenWidth
  } = useWindowSize();
  const [isHydrated, setIsHydrated] = React.useState(false);
  let t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsHydrated(true);
    };
    t6 = [];
    $[0] = t5;
    $[1] = t6;
  } else {
    t5 = $[0];
    t6 = $[1];
  }
  React.useEffect(t5, t6);
  const linkTarget = isHydrated && screenWidth > smallScreenSize ? "_blank" : undefined;
  const shouldShowAllImages = listing.galleryAssets.length > 1;
  const shouldShowGuestCapacity = (!displayStay || !listing.priceDisplay) && (listing.numberOfRooms || listing.guestCapacity) && listing.propertyType !== PropertyType.BoutiqueHotel && listing.propertyType !== PropertyType.BedAndBreakfast;
  let images = [];
  if (!Array.isArray(listing.galleryAssets) || listing.galleryAssets.length > 0) {
    listing.galleryAssets.forEach(galleryAsset => {
      if (galleryAsset.url) {
        images.push({
          url: galleryAsset.url
        });
      }
    });
  }
  images = isHomePageMobile ? images.slice(0, 1) : images.slice(0, 10);
  let t7;
  if ($[2] !== listing.city || $[3] !== listing.stateOrProvinceLongName) {
    t7 = formatListingLocation(listing.city, listing.stateOrProvinceLongName);
    $[2] = listing.city;
    $[3] = listing.stateOrProvinceLongName;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const location = t7;
  let t8;
  if ($[5] !== listing.propertyType || $[6] !== listing.uniquePropertyType) {
    t8 = listing.uniquePropertyType ? enumToStringUniquePropertyTypes(listing.uniquePropertyType) : listing.propertyType ? enumToStringPropertyTypesAbr(listing.propertyType) : null;
    $[5] = listing.propertyType;
    $[6] = listing.uniquePropertyType;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const propertyType = t8;
  const {
    setCurrentListing
  } = useAnalytics();
  const carouselDesktopRef = React.useRef(null);
  const carouselMobileRef = React.useRef(null);
  const [currentSlideDesktop, setCurrentSlideDesktop] = React.useState(0);
  const [currentSlideMobile, setCurrentSlideMobile] = React.useState(0);
  let t9;
  if ($[8] !== listing || $[9] !== setCurrentListing) {
    t9 = () => {
      setCurrentListing(listing);
    };
    $[8] = listing;
    $[9] = setCurrentListing;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleListingClick = t9;
  const handleSlideClick = (e, isDesktopCarousel) => {
    const currentSlide = isDesktopCarousel ? currentSlideDesktop : currentSlideMobile;
    const isOnLastSlide = images.length > 1 && currentSlide === images.length - 1;
    if (isOnLastSlide) {
      e.preventDefault();
      if (isDesktopCarousel) {
        carouselDesktopRef.current?.slickGoTo(0);
      } else {
        carouselMobileRef.current?.slickGoTo(0);
      }
    } else {
      handleListingClick();
    }
  };
  const t10 = forceMobileView && "sm:hidden";
  let t11;
  if ($[11] !== t10) {
    t11 = cx("hidden sm:block w-full h-full", t10);
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = isHomePage && "rounded-xl shadow-md";
  let t13;
  if ($[13] !== containerClassName || $[14] !== t12) {
    t13 = cx("h-full w-full p-2", t12, containerClassName);
    $[13] = containerClassName;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const t14 = isHomePage ? "-mx-2 rounded-b-none" : "aspect-[100/95]";
  let t15;
  if ($[16] !== t14) {
    t15 = cx("relative rounded-xl overflow-hidden", t14);
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== altTag || $[19] !== handleSlideClick || $[20] !== linkTarget || $[21] !== listing.name || $[22] !== priority || $[23] !== showRoute) {
    t16 = (image, index) => <Link to={showRoute} onClick={e_0 => handleSlideClick(e_0, true)} key={index} target={linkTarget} className="block w-full h-full relative aspect-[100/95]" rel="nofollow"><Image fill={true} src={image.url} alt={altTag} placeholder="blur" className="object-center object-cover" loader={index === 0 && priority ? imagekitProgressiveLoader : imagekitLoader} blurDataURL={GRAY_PIXEL_DATA_URL} lazyBoundary="400px" sizes="(min-width: 1536px) calc(16.67vw - 24px), (min-width: 1280px) calc(25vw - 24px), (min-width: 640px) calc(50vw - 32px), calc(100vw - 32px)" {...index === 0 && priority ? {
        priority: true,
        fetchPriority: "high"
      } : {
        loading: "lazy"
      }} data-testid={concatenateAndReplace(`${listing.name}-image-${index}`)} /></Link>;
    $[18] = altTag;
    $[19] = handleSlideClick;
    $[20] = linkTarget;
    $[21] = listing.name;
    $[22] = priority;
    $[23] = showRoute;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== listing.averageSavings) {
    t17 = <SavingsPill savings={listing.averageSavings} />;
    $[25] = listing.averageSavings;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== isFavorited || $[28] !== listing.id || $[29] !== onFavoritedSuccess) {
    t18 = <><div className="absolute top-0.5 -right-0.5 rounded-lg"><FavoriteButton listingID={listing.id} listingIsFavorited={isFavorited} onFavoritedSuccess={onFavoritedSuccess} /></div></>;
    $[27] = isFavorited;
    $[28] = listing.id;
    $[29] = onFavoritedSuccess;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const t19 = `${listing.name}`;
  let t20;
  if ($[31] !== t19) {
    t20 = concatenateAndReplace(t19);
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== listing.name || $[34] !== t20) {
    t21 = <div className="flex-1 text-sm font-semibold line-clamp-1" data-testid={t20}>{listing.name}</div>;
    $[33] = listing.name;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== listing.reviewsAverageRating || $[37] !== listing.reviewsCount) {
    t22 = listing.reviewsAverageRating ? <div className="flex-shrink-0 text-xs font-medium whitespace-nowrap"><div className="flex flex-row space-x-0.5 items-center"><img alt="full rating star" src="/assets/svg/ratings/star-yellow.svg" className="w-3 h-3 pr-[1px]" /><div>{Number.isInteger(listing.reviewsAverageRating) ? listing.reviewsAverageRating?.toFixed(1) : listing.reviewsAverageRating?.toFixed(2)}</div>{listing.reviewsCount && <div>{"( " + listing.reviewsCount + " )"}</div>}</div></div> : null;
    $[36] = listing.reviewsAverageRating;
    $[37] = listing.reviewsCount;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t21 || $[40] !== t22) {
    t23 = <div className="flex flex-row w-full justify-between items-center space-x-1 max-w-md">{t21}{t22}</div>;
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const t24 = `${propertyType}-in-${location}`;
  let t25;
  if ($[42] !== t24) {
    t25 = concatenateAndReplace(t24);
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  const t26 = propertyType ? `${propertyType} in ${location}` : location;
  let t27;
  if ($[44] !== t25 || $[45] !== t26) {
    t27 = <div className="flex flex-row w-full justify-between space-x-1 max-w-md"><div className="flex-shrink text-gray-main text-sm font-light line-clamp-1" data-testid={t25}>{t26}</div></div>;
    $[44] = t25;
    $[45] = t26;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== t23 || $[48] !== t27) {
    t28 = <div className="space-y-1 w-full text-primary-main">{t23}{t27}</div>;
    $[47] = t23;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== listing.guestCapacity || $[51] !== listing.numberOfRooms || $[52] !== shouldShowGuestCapacity) {
    t29 = shouldShowGuestCapacity ? <div className="flex-shrink text-gray-main text-sm font-light line-clamp-1"><div className="flex space-x-1.5">{listing.numberOfRooms ? <div className="flex items-center">{listing.numberOfRooms}{" "}{listing.numberOfRooms > 1 ? " bedrooms" : " bedroom"}</div> : null}{listing.numberOfRooms && listing.guestCapacity ? <div className="flex items-center">•</div> : null}{listing.guestCapacity ? <><div className="flex items-center">{listing.guestCapacity}{" "}{listing.guestCapacity > 1 ? " guests" : " guest"}</div></> : null}</div></div> : null;
    $[50] = listing.guestCapacity;
    $[51] = listing.numberOfRooms;
    $[52] = shouldShowGuestCapacity;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== listing.account.companyName) {
    t30 = listing.account.companyName ? <div className="flex-shrink line-clamp-1 text-gray-main text-sm font-light text-ellipsis overflow-hidden">Hosted by {listing.account.companyName}</div> : null;
    $[54] = listing.account.companyName;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== displayStay || $[57] !== listing.priceDisplay) {
    t31 = displayStay && listing.priceDisplay ? <div className="flex-shrink line-clamp-1 text-gray-main text-sm font-light text-ellipsis overflow-hidden">{formatDateMMMD(listing.priceDisplay.checkInDate)}{" \u2013 "}{formatDateMMMD(listing.priceDisplay.checkOutDate)}</div> : null;
    $[56] = displayStay;
    $[57] = listing.priceDisplay;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== listing.maxPrice || $[60] !== listing.minPrice || $[61] !== listing.priceDisplay) {
    t32 = listing.maxPrice || listing.minPrice || listing.priceDisplay && listing.priceDisplay.baseAmountCents > 100 ? <div className="flex space-x-1 text-primary-main text-sm font-semibold text-ellipsis overflow-hidden pt-0.5">{listing.priceDisplay && listing.priceDisplay.baseAmountCents > 100 ? <><div>{formatWholeDollarAmount(toInteger((listing.priceDisplay.subtotalWithoutTaxesCents || listing.priceDisplay.baseAmountCents) / listing.priceDisplay.nights))}</div><div className="font-medium"> night</div>{true && listing.priceDisplay.subtotalWithoutTaxesCents && <><div className="font-medium"> · </div><div>{formatWholeDollarAmount(toInteger(listing.priceDisplay.subtotalWithoutTaxesCents))}</div><div className="font-medium"> total</div></>}</> : listing.minPrice || listing.maxPrice ? listing.minPrice && listing.maxPrice ? <><div>Averages</div><div>{formatWholeDollarAmount(toInteger((listing.minPrice + listing.maxPrice) / 2))}</div><div className="font-medium"> night</div></> : listing.minPrice ? <><div>Starts at</div><div className="line-through text-gray-main">{formatWholeDollarAmount(toInteger(rateBeforeSavings(listing.minPrice)))}</div><div>{formatWholeDollarAmount(listing.minPrice)}</div><div className="font-medium"> night</div></> : listing.maxPrice ? <><div>Peaks at</div><div className="line-through text-gray-main">{formatWholeDollarAmount(toInteger(rateBeforeSavings(listing.maxPrice)))}</div><div>{formatWholeDollarAmount(listing.maxPrice)}</div><div className="font-medium"> night</div></> : null : null}{listing.maxPrice && !listing.minPrice ? <><div>{`Peaks at ${formatWholeDollarAmount(listing.maxPrice)}`}</div><div className="font-medium"> night</div></> : null}</div> : null;
    $[59] = listing.maxPrice;
    $[60] = listing.minPrice;
    $[61] = listing.priceDisplay;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== t28 || $[64] !== t29 || $[65] !== t30 || $[66] !== t31 || $[67] !== t32) {
    t33 = <div className="flex w-full text-primary-main text-sm font-semibold max-w-md"><div className="flex flex-col w-full justify-between space-y-1">{t28}{t29}{t30}{t31}{t32}</div></div>;
    $[63] = t28;
    $[64] = t29;
    $[65] = t30;
    $[66] = t31;
    $[67] = t32;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== handleListingClick || $[70] !== linkTarget || $[71] !== showRoute || $[72] !== t33) {
    t34 = <Link to={showRoute} onClick={handleListingClick} target={linkTarget} className="no-underline flex flex-row w-full items-top justify-between mt-2 px-1">{t33}</Link>;
    $[69] = handleListingClick;
    $[70] = linkTarget;
    $[71] = showRoute;
    $[72] = t33;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  const t35 = !forceMobileView && "sm:hidden";
  const t36 = isHomePage && "rounded-xl shadow-md overflow-hidden";
  let t37;
  if ($[74] !== containerClassName || $[75] !== t35 || $[76] !== t36) {
    t37 = cx("block sm:block", t35, t36, containerClassName);
    $[74] = containerClassName;
    $[75] = t35;
    $[76] = t36;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  const t38 = !isHomePage && "px-1 pt-0 bg";
  let t39;
  if ($[78] !== t38) {
    t39 = cx("flex h-full", t38);
    $[78] = t38;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  const t40 = isHomePage && "rounded-b-none";
  let t41;
  if ($[80] !== t40) {
    t41 = cx("relative w-full aspect-[100/95] cursor-pointer overflow-hidden rounded-xl ", t40);
    $[80] = t40;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  const T0 = Carousel;
  const t42 = priority ? "progressive" : "ondemand";
  let t43;
  if ($[82] !== altTag || $[83] !== handleSlideClick || $[84] !== linkTarget || $[85] !== listing.name || $[86] !== showRoute) {
    t43 = (image_0, index_0) => <Link to={showRoute} onClick={e_1 => handleSlideClick(e_1, false)} key={index_0} target={linkTarget} className="block w-full h-full relative aspect-[100/95]" rel="nofollow"><Image fill={true} src={image_0.url} alt={altTag} placeholder="blur" className="object-center object-cover" loader={imagekitLoader} blurDataURL={GRAY_PIXEL_DATA_URL} lazyBoundary="400px" sizes="(min-width: 1536px) calc(16.67vw - 24px), (min-width: 1280px) calc(25vw - 24px), (min-width: 640px) calc(50vw - 32px), calc(100vw - 32px)" loading="lazy" data-testid={concatenateAndReplace(`${listing.name}-image-${index_0}`)} /></Link>;
    $[82] = altTag;
    $[83] = handleSlideClick;
    $[84] = linkTarget;
    $[85] = listing.name;
    $[86] = showRoute;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  const t44 = images.map(t43);
  let t45;
  if ($[88] !== T0 || $[89] !== isHomePage || $[90] !== setCurrentSlideMobile || $[91] !== shouldShowAllImages || $[92] !== t42 || $[93] !== t44) {
    t45 = <T0 ref={carouselMobileRef} showDots={shouldShowAllImages} showArrows={shouldShowAllImages} loadingStrategy={t42} disableSwipe={isHomePage} onSlideChange={setCurrentSlideMobile}>{t44}</T0>;
    $[88] = T0;
    $[89] = isHomePage;
    $[90] = setCurrentSlideMobile;
    $[91] = shouldShowAllImages;
    $[92] = t42;
    $[93] = t44;
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] !== featured || $[96] !== listing.averageSavings) {
    t46 = <SavingsPill savings={listing.averageSavings} featured={featured} />;
    $[95] = featured;
    $[96] = listing.averageSavings;
    $[97] = t46;
  } else {
    t46 = $[97];
  }
  let t47;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = cx("absolute -right-0.5 rounded-lg top-0.5");
    $[98] = t47;
  } else {
    t47 = $[98];
  }
  let t48;
  if ($[99] !== isFavorited || $[100] !== listing.id || $[101] !== onFavoritedSuccess) {
    t48 = <div className={t47}><FavoriteButton listingID={listing.id} listingIsFavorited={isFavorited} onFavoritedSuccess={onFavoritedSuccess} size="md" /></div>;
    $[99] = isFavorited;
    $[100] = listing.id;
    $[101] = onFavoritedSuccess;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] !== t45 || $[104] !== t46 || $[105] !== t48) {
    t49 = <div>{t45}{t46}{t48}</div>;
    $[103] = t45;
    $[104] = t46;
    $[105] = t48;
    $[106] = t49;
  } else {
    t49 = $[106];
  }
  let t50;
  if ($[107] !== t41 || $[108] !== t49) {
    t50 = <div className={t41}>{t49}</div>;
    $[107] = t41;
    $[108] = t49;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== t39 || $[111] !== t50) {
    t51 = <div className={t39}>{t50}</div>;
    $[110] = t39;
    $[111] = t50;
    $[112] = t51;
  } else {
    t51 = $[112];
  }
  const t52 = isHomePage && "px-2 pb-2";
  let t53;
  if ($[113] !== t52) {
    t53 = cx("flex flex-row items-top justify-between mt-2", t52);
    $[113] = t52;
    $[114] = t53;
  } else {
    t53 = $[114];
  }
  const t54 = `${listing.name}`;
  let t55;
  if ($[115] !== t54) {
    t55 = concatenateAndReplace(t54);
    $[115] = t54;
    $[116] = t55;
  } else {
    t55 = $[116];
  }
  let t56;
  if ($[117] !== listing.name || $[118] !== t55) {
    t56 = <div className="flex-1 text-sm font-semibold line-clamp-1" data-testid={t55}>{listing.name}</div>;
    $[117] = listing.name;
    $[118] = t55;
    $[119] = t56;
  } else {
    t56 = $[119];
  }
  let t57;
  if ($[120] !== listing.reviewsAverageRating || $[121] !== listing.reviewsCount) {
    t57 = listing.reviewsAverageRating ? <div className="flex-shrink-0 text-xs font-medium whitespace-nowrap"><div className="flex flex-row items-center space-x-0.5"><img alt="full rating star" src="/assets/svg/ratings/star-yellow.svg" className="w-3 h-3 pr-[1px]" /><div>{Number.isInteger(listing.reviewsAverageRating) ? listing.reviewsAverageRating?.toFixed(1) : listing.reviewsAverageRating?.toFixed(2)}</div>{listing.reviewsCount && <div>{"( " + listing.reviewsCount + " )"}</div>}</div></div> : null;
    $[120] = listing.reviewsAverageRating;
    $[121] = listing.reviewsCount;
    $[122] = t57;
  } else {
    t57 = $[122];
  }
  let t58;
  if ($[123] !== t56 || $[124] !== t57) {
    t58 = <div className="flex flex-row w-full items-center justify-between space-x-1">{t56}{t57}</div>;
    $[123] = t56;
    $[124] = t57;
    $[125] = t58;
  } else {
    t58 = $[125];
  }
  const t59 = propertyType ? `${propertyType} in ${location}` : location;
  let t60;
  if ($[126] !== t59) {
    t60 = <div className="flex flex-row w-full justify-between space-x-1"><div className="flex-shrink text-gray-main text-sm font-light line-clamp-1">{t59}</div></div>;
    $[126] = t59;
    $[127] = t60;
  } else {
    t60 = $[127];
  }
  let t61;
  if ($[128] !== listing.guestCapacity || $[129] !== listing.numberOfRooms || $[130] !== shouldShowGuestCapacity) {
    t61 = shouldShowGuestCapacity ? <div className="flex-shrink text-gray-main text-sm font-light line-clamp-1"><div className="flex space-x-1.5">{listing.numberOfRooms ? <div className="flex items-center">{listing.numberOfRooms}{" "}{listing.numberOfRooms > 1 ? " bedrooms" : " bedroom"}</div> : null}{listing.numberOfRooms && listing.guestCapacity ? <div className="flex items-center">•</div> : null}{listing.guestCapacity ? <><div className="flex items-center">{listing.guestCapacity}{" "}{listing.guestCapacity > 1 ? " guests" : " guest"}</div></> : null}</div></div> : null;
    $[128] = listing.guestCapacity;
    $[129] = listing.numberOfRooms;
    $[130] = shouldShowGuestCapacity;
    $[131] = t61;
  } else {
    t61 = $[131];
  }
  let t62;
  if ($[132] !== listing.account.companyName) {
    t62 = listing.account.companyName ? <div className="flex-shrink line-clamp-1 text-gray-main text-sm font-light text-ellipsis overflow-hidden">Hosted by {listing.account.companyName}</div> : null;
    $[132] = listing.account.companyName;
    $[133] = t62;
  } else {
    t62 = $[133];
  }
  let t63;
  if ($[134] !== displayStay || $[135] !== listing.priceDisplay) {
    t63 = displayStay && listing.priceDisplay ? <div className="flex-shrink line-clamp-1 text-gray-main text-sm font-light text-ellipsis overflow-hidden">{formatDateMMMD(listing.priceDisplay.checkInDate)}{" \u2013 "}{formatDateMMMD(listing.priceDisplay.checkOutDate)}</div> : null;
    $[134] = displayStay;
    $[135] = listing.priceDisplay;
    $[136] = t63;
  } else {
    t63 = $[136];
  }
  let t64;
  if ($[137] !== listing.maxPrice || $[138] !== listing.minPrice || $[139] !== listing.priceDisplay) {
    t64 = listing.maxPrice || listing.minPrice || listing.priceDisplay && listing.priceDisplay.baseAmountCents > 100 ? <div className="text-primary-main text-sm font-semibold mt-0.5"><div className="flex space-x-1 text-primary-main text-sm font-semibold text-ellipsis overflow-hidden pt-0.5">{listing.priceDisplay && listing.priceDisplay.baseAmountCents > 100 ? <><div>{formatWholeDollarAmount(toInteger((listing.priceDisplay.subtotalWithoutTaxesCents || listing.priceDisplay.baseAmountCents) / listing.priceDisplay.nights))}</div><div className="font-medium"> night</div>{true && listing.priceDisplay.subtotalWithoutTaxesCents && <><div className="font-medium"> · </div><div>{formatWholeDollarAmount(toInteger(listing.priceDisplay.subtotalWithoutTaxesCents))}</div><div className="font-medium"> total</div></>}</> : listing.minPrice || listing.maxPrice ? listing.minPrice && listing.maxPrice ? <><div>Averages</div><div>{formatWholeDollarAmount(toInteger((listing.minPrice + listing.maxPrice) / 2))}</div><div className="font-medium"> night</div></> : listing.minPrice ? <><div>Starts at</div><div className="line-through text-gray-main">{formatWholeDollarAmount(toInteger(rateBeforeSavings(listing.minPrice)))}</div><div>{formatWholeDollarAmount(listing.minPrice)}</div><div className="font-medium"> night</div></> : listing.maxPrice ? <><div>Peaks at</div><div className="line-through text-gray-main">{formatWholeDollarAmount(toInteger(rateBeforeSavings(listing.maxPrice)))}</div><div>{formatWholeDollarAmount(listing.maxPrice)}</div><div className="font-medium"> night</div></> : null : null}{listing.maxPrice && !listing.minPrice ? <><div>{`Peaks at ${formatWholeDollarAmount(listing.maxPrice)}`}</div><div className="font-medium"> night</div></> : null}</div></div> : null;
    $[137] = listing.maxPrice;
    $[138] = listing.minPrice;
    $[139] = listing.priceDisplay;
    $[140] = t64;
  } else {
    t64 = $[140];
  }
  let t65;
  if ($[141] !== t58 || $[142] !== t60 || $[143] !== t61 || $[144] !== t62 || $[145] !== t63 || $[146] !== t64) {
    t65 = <div className="space-y-0.5 w-full text-primary-main">{t58}{t60}{t61}{t62}{t63}{t64}</div>;
    $[141] = t58;
    $[142] = t60;
    $[143] = t61;
    $[144] = t62;
    $[145] = t63;
    $[146] = t64;
    $[147] = t65;
  } else {
    t65 = $[147];
  }
  let t66;
  if ($[148] !== handleListingClick || $[149] !== showRoute || $[150] !== t65) {
    t66 = <div className="w-full"><div className="flex flex-col w-full justify-between mt-1.5"><a href={showRoute} onClick={handleListingClick}>{t65}</a></div></div>;
    $[148] = handleListingClick;
    $[149] = showRoute;
    $[150] = t65;
    $[151] = t66;
  } else {
    t66 = $[151];
  }
  let t67;
  if ($[152] !== t53 || $[153] !== t66) {
    t67 = <div className={t53}>{t66}</div>;
    $[152] = t53;
    $[153] = t66;
    $[154] = t67;
  } else {
    t67 = $[154];
  }
  let t68;
  if ($[155] !== t37 || $[156] !== t51 || $[157] !== t67) {
    t68 = <div className={t37}>{t51}{t67}</div>;
    $[155] = t37;
    $[156] = t51;
    $[157] = t67;
    $[158] = t68;
  } else {
    t68 = $[158];
  }
  return <AnalyticsRef name="listing"><div className={t11}><div className={t13}><div className="w-full"><div className={t15}><div className="w-full"><Carousel ref={carouselDesktopRef} showDots={shouldShowAllImages} showArrows={shouldShowAllImages} loadingStrategy={priority ? "progressive" : "ondemand"} onSlideChange={setCurrentSlideDesktop}>{images.map(t16)}</Carousel>{t17}{t18}</div></div>{t34}</div></div></div>{t68}</AnalyticsRef>;
};